import { FeedTypeEnum } from '@orijinworks/frontend-commons';
import moment from 'moment';
import {
  PAGINATION_CONSTANTS, MEMBERSHIP_TYPE, TagEnum, ROLE, ATTACHMENT_UPLOAD,
  ATTACHMENT_FLOW_STATUS, MIME_TYPE_FORM_IO, RELATIVE_URL_MIME_TYPE
} from '../constants';
import { USER_CONTEXT } from '../../../../constants';
import { ADMIN_ATTACHMENT_RENDER_CONTENT_BY_FILE_TYPE } from './adminAttachmentChipDisplay';


export const DATETIME_FORMAT = 'MM/DD/YYYY hh:mm A';
/**
 * Clean data mappers and utilities for Feeds functionality
 * Handles consistent transformation between API responses (camelCase) and Socket events (snake_case)
 * to the display format expected by frontend-commons components
 * Also includes utility functions for message limit calculations
 */

/**
 * Map feed type string to enum
 */
export const mapFeedTypeToEnum = (feedType) => {
  const upperType = feedType?.toUpperCase();
  switch (upperType) {
    case 'DM':
      return FeedTypeEnum.dm;
    case 'ANNOUNCEMENT':
      return FeedTypeEnum.announcement;
    case 'CHANNEL':
      return FeedTypeEnum.channel;
    default:
      return FeedTypeEnum.dm;
  }
};

/**
 * Map feed type enum back to string
 */
export const mapFeedEnumToType = (feedTypeEnum) => {
  switch (feedTypeEnum) {
    case FeedTypeEnum.dm:
      return 'DM';
    case FeedTypeEnum.announcement:
      return 'ANNOUNCEMENT';
    case FeedTypeEnum.channel:
      return 'CHANNEL';
    default:
      return 'DM';
  }
};


/**
 * Default metadata for tags (styles, ordering, static content)
 */
const TAG_DEFAULTS = {
  [TagEnum.New]: { title: 'New', order: 1 },
  [TagEnum.Private]: { title: 'Private', order: 2 },
  [TagEnum.Locked]: { title: 'Locked', order: 3 },
  [TagEnum.ViewOnly]: { title: 'View Only', order: 4 },
  [TagEnum.MessageLimit]: { order: 5 },
  [TagEnum.MonitoredByAdmin]: { title: 'Messages are monitored', order: 6 },
};
/**`
 * Helper to create a tag object with defaults and overrides
 */
export const createTag = (type, props = {}) => ({
  type,
  ...(TAG_DEFAULTS[type] || {}),
  ...props,
});

/**
 * Returns tags without entries whose `type` equals tagType (e.g. TagEnum.New when marking read).
 * Non-array / nullish `tags` yields [] — same as `tags?.filter(...) || []` for invalid shapes.
 * @param {Array<{ type?: * }>|undefined|null|*} tags
 * @param {*} tagType
 * @returns {Array}
 */
export const removeTagTypeFromTags = (tags, tagType) => (Array.isArray(tags) ? tags : []).filter((tag) => tag.type !== tagType);

// --- Predicate helpers (pure, small, readable) ---
const hasPermissions = (p) => Boolean(p);

const isMessagingDisabled = (p) => p?.messaging_disabled === true;

const learnersCantSend = (p) => p?.learners_can_send_message === false;

const hasMessageLimit = (p) => p?.limit_learner_messages === true;

const isConfidentialFeed = (feed) =>
  feed?.is_confidential === true || feed?.isConfidential === true;

const isLockedFeed = (feed) => feed?.isLocked === true;

// --- Tag builders (pure) ---
const confidentialityTag = (isConfidential) =>
  createTag(isConfidential ? TagEnum.Private : TagEnum.MonitoredByAdmin);

const lockedTag = () => createTag(TagEnum.Locked);

const viewOnlyTag = () => createTag(TagEnum.ViewOnly);

const learnerViewOnlyTag = () =>
  createTag(TagEnum.LearnerViewOnly, { title: "Learner view only" });

const messageLimitTag = (p) =>
  createTag(TagEnum.MessageLimit, {
    title: `Message Limit ${p?.learner_message_limit}`,
  });


/**
 * Strip 'New' tags from the tags array
 * Used to remove 'New' indicators after announcements have been marked as read
 */
export const stripNewTags = (tags = []) => {
  if (!Array.isArray(tags)) {
    return [];
  }

  const filtered = tags.filter((tag) => {
    const identifier = (tag?.type || tag?.title || '').toString().toLowerCase();
    return identifier !== 'new';
  });

  return filtered.length === tags.length ? tags : filtered;
};

export const isAnnouncementUnread = (announcement = {}) => {
  // Handle null or undefined announcements
  if (!announcement || typeof announcement !== 'object') {
    return true;
  }

  // Treat as unread only if readAt is explicitly present and null
  return !('readAt' in announcement) || announcement.readAt === null || announcement.readAt === undefined;
};

/**
 * Update pending announcements to mark as read
 * Filters unread announcements and either replaces or appends to the pending list
 * 
 * @param {Array} currentPending - Current list of pending announcements to mark as read
 * @param {Array} newAnnouncements - New announcements to process
 * @param {boolean} shouldReset - If true, replace current pending list; if false, append to it
 * @returns {Array} Updated list of pending announcements to mark as read
 */
export const updatePendingMarkAsReadAnnouncements = (currentPending = [], newAnnouncements = [], shouldReset = false) => {
  // Normalize currentPending to ensure it's always an array
  const normalizedPending = Array.isArray(currentPending) ? currentPending : [];

  if (!Array.isArray(newAnnouncements) || newAnnouncements.length === 0) {
    return shouldReset ? [] : normalizedPending;
  }

  const unreadAnnouncements = newAnnouncements.filter((announcement) => isAnnouncementUnread(announcement));

  if (shouldReset) {
    // For initial load, track all unread announcements
    return unreadAnnouncements;
  } else {
    // For load more, also track new announcements
    return [...normalizedPending, ...unreadAnnouncements];
  }
};

export const mapCreateResponseToFeed = (data, currentUserKey, onFeedClickHandler) => {
  const { membersInfo } = data;
  const tags = [];
  tags.push(createTag(TagEnum.New));
  if (data.isConfidential) tags.push(createTag(TagEnum.Private));
  return {
    id: data.id,
    type: mapFeedTypeToEnum(data.feedType),
    title: buildMemberNames(membersInfo, currentUserKey, MEMBERSHIP_TYPE.PERSON),
    description: generateFeedDescription({ memberInfo: membersInfo }),
    from: data.name,
    to: data.name,
    timestamp: moment().toISOString(),
    isNew: true,
    isHidden: false,
    isViewAllVisible: false,
    tags: tags,
    settings: [],
    onFeedSelectHandler: onFeedClickHandler,
    feedName: data.name,
    unreadCount: 0,
    senderKey: USER_CONTEXT.personId,
  };
}


/**
 * Check if a member is a Learner by checking roleName on member or locations
 */
const isLearnerMember = (member) => {
  if (member.roleName === ROLE.LEARNER) {
    return true;
  }
  return Array.isArray(member.locations) &&
    member.locations.some(location => location.roleName === ROLE.LEARNER);
};

/**
 * Extract docId from a learner member
 */
const getLearnerDocId = (learner) => {
  const docId = learner?.docId;
  return docId?.toString().trim() || null;
};

/**
 * Extract active location name from a learner's locations
 */
const getLearnerActiveLocationName = (learner) => {
  const activeLocation = learner?.locations?.find(location => location.active);
  return activeLocation?.locationName?.trim() || null;
};


const isNameMatching = (name, searchString) => {
  return name.includes(searchString);
}

const isDocIdMatching = (memberInfo, searchString) => {
  return memberInfo.some(member => member.docId && member.docId.includes(searchString));
}


const flatLocationFromMemberInfo = (memberInfo) => {
  return memberInfo.flatMap(member => member.locations);
}

const isLocationMatching = (locations, locationKeySet) => {
  return locations
    .filter(location => location.active)
    .some(location => locationKeySet.has(location.locationId));
}

export const isNameDocIdLocationsAvailableInSocketData = (socketData, searchString, locationKeySet) => {
  if (searchString.trim() === '' && locationKeySet.size === 0) {
    return true;
  }
  const { feed_name, members_info } = socketData;
  const nameMatching = searchString === '' || isNameMatching(feed_name, searchString);
  const docIdMatching = searchString === '' || isDocIdMatching(members_info, searchString);
  const locationMatching = locationKeySet.size === 0 || isLocationMatching(flatLocationFromMemberInfo(members_info), locationKeySet);
  if ((nameMatching || docIdMatching) && locationMatching) {
    return true;
  }
  return false;
}
export const generateFeedDescription = ({ memberInfo }) => {
  if (!Array.isArray(memberInfo) || memberInfo.length === 0) {
    return '';
  }

  // Collect unique docIds and location names from learners
  const learners = memberInfo.filter(isLearnerMember);
  const docIds = new Set(learners.map(getLearnerDocId).filter(docId => docId !== null));
  const locationNames = new Set(learners.map(getLearnerActiveLocationName).filter(locationName => locationName !== null));

  // Build description parts
  const docIdPart = docIds.size > 0
    ? `DOC ID: ${Array.from(docIds).join(', ')}`
    : 'DOC ID: N/A';

  const locationPart = locationNames.size > 0
    ? ` | ${Array.from(locationNames).join(', ')}`
    : '';

  return `${docIdPart}${locationPart}`.trim();
};
/**
 * Transform API feed response to display format
 * Handles camelCase API responses from getFeedsWithMessages, getDMFeeds, etc.
 *
 * @param {Object} apiFeedData - API response data (camelCase)
 * @param {string} currentUserKey - Current user's identifier
 * @param {string} context - Display context ('all' or 'dm')
 * @returns {Object} Display format feed object
 */
export const transformApiFeedToDisplay = (apiFeedData, currentUserKey, context = 'all') => {
  const {
    id,
    feedId,
    senderName,
    senderPersonKey,
    senderId,
    feedType,
    feedName,
    name,
    createdAt,
    lastMessageAt,
    to,
    isUnread,
    unreadCount,
    isConfidential,
    feedConfidential,
    membersInfo = [],
    permissions,
    isLocked = false,
  } = apiFeedData;

  // Determine identifiers
  const feedIdentifier = feedId || id;
  const senderKey = senderPersonKey || senderId;
  const isCurrentUserSender = senderKey && currentUserKey && String(senderKey) === String(currentUserKey);
  const isFeedConfidential = feedConfidential || isConfidential;
  const messaging_disabled = permissions?.messaging_disabled;

  // Determine display names based on context
  let fromValue, toValue;
  if (context === 'dm' && feedType === 'DM') {
    // In DM context, show conversation name as from
    fromValue = feedName || name || 'Direct Message';
    toValue = 'You';
  } else {
    // In All Feeds context, show sender name
    fromValue = isCurrentUserSender ? 'You' : senderName || 'System';
    toValue = isCurrentUserSender ? to || 'Recipients' : 'You';
  }

  // Create tags
  const tags = [];
  if (isUnread || unreadCount > 0) tags.push(createTag(TagEnum.New));
  if (isFeedConfidential) tags.push(createTag(TagEnum.Private));
  if (isLocked) tags.push(createTag(TagEnum.Locked));
  if (!isLocked && messaging_disabled) tags.push(createTag(TagEnum.ViewOnly));


  const isGroupFeed = membersInfo?.length > 2;
  const feedTitle = buildMemberNames(membersInfo, currentUserKey, MEMBERSHIP_TYPE.PERSON);

  return {
    id: feedIdentifier,
    type: isGroupFeed ? FeedTypeEnum.group : mapFeedTypeToEnum(feedType),
    title: feedTitle || (feedType?.toUpperCase() === 'DM' ? 'Direct Message' : 'Untitled Feed'),
    description: generateFeedDescription({ memberInfo: membersInfo }),
    from: fromValue,
    to: toValue,
    timestamp: lastMessageAt || createdAt,
    isNew: isUnread || unreadCount > 0 || false,
    isHidden: false,
    isViewAllVisible: context === 'all',
    tags,
    settings: [],
    onHideHandler: () => { },
    onViewFullHandler: () => { },
    feedName: feedName || name,
    unreadCount: unreadCount || 0,
    senderKey,
    membersInfo,
  };
};

/**
 * Map API announcement attachment to display shape for AttachmentChip
 * Normalizes attachmentId -> id so frontend-commons and preview hook receive consistent shape
 * @param {Object} att - API attachment: { attachmentId, filename, caption, mimeType, fileSize, uploadedAt, formattedFileSize }
 * @returns {Object} { id, filename, caption, metadata } for IAnnouncementAttachmentMetadata
 */
const mapAnnouncementAttachmentToDisplay = (att) => {
  if (!att) return null;
  const id = att.attachmentId || att.id;
  if (!id) return null;
  const filename = att.filename || att.fileName || '';
  const uploadedAt = att.uploadedAt || att.createdAt;
  const uploadDateStr = uploadedAt ? moment(uploadedAt).format('MMM D, YYYY') : '';
  const sizeStr = att.formattedFileSize || (att.fileSize ? `${(att.fileSize / 1024).toFixed(1)} KB` : '');
  const metadata = [uploadDateStr, sizeStr].filter(Boolean).join(' • ') || undefined;
  const metaObj = att.metadata != null && typeof att.metadata === 'object' ? att.metadata : null;
  const formIdRaw = metaObj && (metaObj.formId ?? metaObj.form_id);
  const formDefinitionId = formIdRaw != null ? String(formIdRaw) : undefined;
  const rawMime = att.mimeType || att.mime_type;
  const baseMime = typeof rawMime === 'string' ? rawMime.split(';')[0].trim().toLowerCase() : '';
  const mimeType =
    baseMime === MIME_TYPE_FORM_IO.toLowerCase() || formDefinitionId ? MIME_TYPE_FORM_IO : rawMime || undefined;
  return {
    id,
    filename,
    caption: att.caption ?? null,
    mimeType,
    fileSize: att.fileSize,
    uploadedAt,
    metadata: metadata || att.caption || undefined,
    renderContentByFileType: ADMIN_ATTACHMENT_RENDER_CONTENT_BY_FILE_TYPE,
    formDefinitionId,
  };
};

/**
 * Transform API announcement response to display format
 * Handles announcement-specific fields from API responses (camelCase)
 *
 * @param {Object} apiAnnouncementData - API announcement response data
 * @returns {Object} Display format announcement object
 */
export const transformApiAnnouncementToDisplay = (apiAnnouncementData) => {
  const {
    id,
    subject,
    body,
    senderName,
    createdBy,
    createdAt,
    membersInfo = [],
    metadata = {},
    readAt,
    attachments: rawAttachments = [],
  } = apiAnnouncementData;

  const isUnread = isAnnouncementUnread(apiAnnouncementData);
  const tags = isUnread ? [{ title: 'New', type: TagEnum.New }] : [];
  const attachments = Array.isArray(rawAttachments)
    ? rawAttachments.map(mapAnnouncementAttachmentToDisplay).filter(Boolean)
    : [];

  return {
    id,
    type: mapFeedTypeToEnum('ANNOUNCEMENT'),
    title: subject || 'Announcement',
    description: body || '',
    from: senderName || '',
    to: buildRecipientList(membersInfo),
    timestamp: createdAt || new Date().toISOString(),
    senderKey: createdBy,
    metadata,
    membersInfo,
    readAt,
    isNew: isUnread,
    tags,
    attachments,
  };
};

/**
 * Transform socket event to announcement display format
 * Handles snake_case socket events for announcements
 *
 * @param {Object} socketEventData - Socket event data (snake_case)
 * @returns {Object} Display format announcement object
 */
export const transformSocketEventToAnnouncement = (socketEventData) => {
  const {
    announcement_id,
    subject,
    body,
    sender_id,
    timestamp,
    members_info = [],
    metadata = {},
    sender_name
  } = socketEventData;

  // Extract subject from metadata if not directly available
  const announcementSubject = subject || metadata?.subject || 'Announcement';
  const announcementId = announcement_id || metadata?.announcement_id;

  return {
    id: announcementId,
    type: mapFeedTypeToEnum('ANNOUNCEMENT'),
    title: announcementSubject,
    description: body || '',
    from: sender_name || '',
    to: buildRecipientList(members_info),
    timestamp: timestamp || new Date().toISOString(),
    senderKey: sender_id,
    isNew: true,
    tags: [{ title: 'New', type: TagEnum.New }],
    metadata,
  };
};

/**
 * Transform API conversation response to display format
 * Specialized for DM conversation lists
 *
 * @param {Object} apiConversationData - API response data for conversation
 * @param {string} currentUserKey - Current user's identifier
 * @param {string} context - Display context ('dm')
 * @returns {Object} Display format conversation object
 */
export const transformApiConversationToDisplay = (apiConversationData, currentUserKey, context = 'dm') => {
  // Use the general transformer but ensure it's treated as a conversation
  return transformApiFeedToDisplay(apiConversationData, currentUserKey, context);
};

/**
 * Transform socket event to display feed format
 * Handles snake_case socket events (feed_created, feed_message_sent)
 *
 * @param {Object} socketEventData - Socket event data (snake_case)
 * @param {string} currentUserKey - Current user's identifier
 * @param {string} context - Display context ('all' or 'dm')
 * @returns {Object} Display format feed object
 */
export const transformSocketEventToFeed = (socketEventData, currentUserKey, context = 'all') => {
  const { feed_id, sender_id, sender_name, feed_name, timestamp, feed_type, metadata = {}, members_info = [] } = socketEventData;

  const { is_confidential } = metadata;

  // Determine if current user is sender
  const isCurrentUserSender = sender_id === currentUserKey;

  // Determine display names
  let fromValue, toValue;
  if (context === 'dm' && feed_type.toUpperCase() === 'DM') {
    fromValue = feed_name || sender_name || 'Direct Message';
    toValue = 'You';
  } else {
    fromValue = isCurrentUserSender ? 'You' : sender_name || 'Unknown';
    toValue = isCurrentUserSender ? 'Recipients' : 'You';
  }

  // Create tags - new messages are always marked as unread
  const isFeedConfidential = is_confidential === 'true' || is_confidential === true;
  const isUnread = true;
  const unreadCount = 1;
  const tags = [];
  if (isUnread || unreadCount > 0) tags.push(createTag(TagEnum.New));
  if (isFeedConfidential) tags.push(createTag(TagEnum.Private));

  const isGroupFeed = members_info?.length > 2;
  const feedTitle = buildMemberNames(members_info, currentUserKey, MEMBERSHIP_TYPE.PERSON);

  return {
    id: feed_id,
    type: isGroupFeed ? FeedTypeEnum.group : mapFeedTypeToEnum(feed_type),
    title: feedTitle || 'Direct Message',
    description: generateFeedDescription({ memberInfo: members_info }),
    from: fromValue,
    to: toValue,
    timestamp: timestamp || new Date().toISOString(),
    isNew: true,
    isHidden: false,
    isViewAllVisible: context === 'all',
    tags,
    settings: [],
    onHideHandler: () => { },
    onViewFullHandler: () => { },
    feedName: feed_name,
    unreadCount: 1,
    senderKey: sender_id,
  };
};

/**
 * Transform socket event to conversation format
 * Specialized for creating new conversations from socket events
 *
 * @param {Object} socketEventData - Socket event data (snake_case)
 * @param {string} currentUserKey - Current user's identifier
 * @param {boolean} isCurrentlyOpen - Whether this conversation is currently being viewed
 * @returns {Object} Display format conversation object
 */
export const transformSocketEventToConversation = (socketEventData, currentUserKey, isCurrentlyOpen = false) => {
  const baseFeed = transformSocketEventToFeed(socketEventData, currentUserKey, 'dm');

  // Adjust properties for conversation context
  return {
    ...baseFeed,
    isNew: !isCurrentlyOpen,
    unreadCount: isCurrentlyOpen ? 0 : 1,
    tags: isCurrentlyOpen ? removeTagTypeFromTags(baseFeed.tags, TagEnum.New) : baseFeed.tags,
    isViewAllVisible: false, // Conversations don't show view all
  };
};

/**
 * Attach menu props to message with messageId wrapped in onClick handlers
 * 
 * @param {Object} menuProps - Complete menu props object with IVerticalMenuProps structure
 * @param {string|number} messageId - Message ID to pass to onClick handlers
 * @returns {Object|undefined} Menu props object with onClick handlers wrapped with messageId, or undefined if no menuProps
 */
const attachMenuPropsToMessage = (menuProps, messageId) => {
  if (!menuProps || !menuProps.options || !Array.isArray(menuProps.options) || menuProps.options.length === 0) {
    return undefined;
  }

  return {
    ...menuProps,
    options: menuProps.options.map((option) => ({
      ...option,
      onClick: typeof option.onClick === 'function'
        ? () => option.onClick(messageId) // Wrap onClick to pass messageId
        : option.onClick,
    })),
  };
};

/**
 * Map API attachment (metadata only, no URL) to display shape for AttachmentChip
 * @param {Object} att - API attachment: { attachmentId/fileId, filename, caption, mimeType, fileSize, uploadedAt, formattedFileSize }
 * @returns {Object} { fileName, metadata, fileId }
 */
const mapAttachmentToDisplay = (att) => {
  if (!att) return null;
  const filename = att.filename || att.fileName || '';
  const uploadedAt = att.uploadedAt || att.createdAt;
  const uploadDateStr = uploadedAt
    ? moment(uploadedAt).format('MMM D, YYYY')
    : '';
  const sizeStr = att.formattedFileSize || (att.fileSize ? `${(att.fileSize / 1024).toFixed(1)} KB` : '');
  const metadata = [uploadDateStr, sizeStr].filter(Boolean).join(' • ');
  const metaObj =
    att.metadata != null && typeof att.metadata === 'object' ? att.metadata : null;
  const formDefinitionId =
    metaObj && metaObj.formId != null ? String(metaObj.formId) : undefined;
  const mimeType = att.mimeType || undefined;
  const base = {
    fileName: filename,
    metadata: metadata || undefined,
    fileId: att.attachmentId || att.fileId || att.id,
    renderContentByFileType: ADMIN_ATTACHMENT_RENDER_CONTENT_BY_FILE_TYPE,
    mimeType,
    formDefinitionId,
  };
  if (isRelativeUrlMimeType(mimeType)) {
    return {
      ...base,
      disabled: true,
    };
  }
  return base;
};

/**
 * Process attachment with all computed values including dynamic state
 * This replaces getProcessedAttachmentProps from frontend-commons
 * 
 * @param {Object} attachment - Attachment object: { fileName, metadata, fileId/attachmentId, fileUrl? }
 * @param {Object} attachmentState - Dynamic state: { fileUrl?, loading, isModalOpen, error? }
 * @returns {Object} Fully processed attachment with all computed properties
 */
export const processAttachmentWithState = (attachment, attachmentState = {}) => {
  if (!attachment) return null;

  // Compute fileId (attachmentId maps to fileId for AttachmentChip compatibility)
  const fileId = attachment.attachmentId || attachment.fileId || attachment.fileName;

  // Merge attachment with state
  const fileUrl = attachmentState?.fileUrl || attachment.fileUrl;
  const loading = attachmentState?.loading || false;
  const isModalOpen = attachmentState?.isModalOpen || false;
  const hasError = Boolean(attachmentState?.error);
  const errorMessage = attachmentState?.error;

  return {
    ...attachment,
    fileId, // Use fileId instead of attachmentId
    fileUrl,
    loading,
    isModalOpen,
    hasError,
    errorMessage,
  };
};

/**
 * Transform message data to display format
 * Handles both API responses (camelCase) and socket events (snake_case)
 *
 * @param {Object} messageData - Message data from API or socket
 * @param {string} currentUserKey - Current user's identifier
 * @param {Object} menuProps - Optional menu props object with IVerticalMenuProps structure
 * @returns {Object} Display format message object
 */
export const transformSocketEventToMessage = (messageData, currentUserKey, menuProps) => {
  // Handle nested message structure from API responses
  const msgData = messageData.message || messageData;

  // Extract data handling both camelCase (API) and snake_case (socket)
  const messageId = msgData.messageId || msgData.message_id || msgData.id;
  const senderId = msgData.senderPersonKey || msgData.sender_person_key || msgData.sender_id;
  const senderFirstName = msgData.senderName || msgData.sender_name || msgData.firstName || '';
  const senderLastName = msgData.senderLastName || msgData.sender_last_name || msgData.lastName || '';
  const messageBody = msgData.body || msgData.content || '';
  const messageTimestamp = msgData.createdAt || msgData.created_at || msgData.timestamp || new Date().toISOString();

  // Determine if current user is the sender
  // Compare as strings to handle both number and string types
  // Only compare if both values are defined (not null/undefined)
  const isSender = senderId != null && currentUserKey != null
    ? String(senderId) === String(currentUserKey)
    : false;

  const transformedMessage = {
    id: messageId,
    userId: senderId,
    firstName: senderFirstName,
    lastName: senderLastName,
    msgHTML: messageBody,
    isSender,
    isSent: true,
    isHidden: false,
    timestamp: messageTimestamp,
    isNew: true,
    created: false,
  };

  // Attach menu props with messageId wrapped in onClick handlers
  const attachedMenuProps = attachMenuPropsToMessage(menuProps, messageId);
  if (attachedMenuProps) {
    transformedMessage.menuProps = attachedMenuProps;
  }

  // Map attachment metadata (no presigned URLs; view/preview is on-demand per CC-7835)
  const rawAttachments = msgData.attachments || [];
  if (Array.isArray(rawAttachments) && rawAttachments.length > 0) {
    transformedMessage.attachments = rawAttachments
      .map(mapAttachmentToDisplay)
      .filter(Boolean);
  }

  return transformedMessage;
};

/**
 * Transform API message response to display format
 * Specialized for API message responses (already camelCase)
 *
 * @param {Object} apiMessageData - API message response
 * @param {string} currentUserKey - Current user's identifier
 * @param {Object} menuProps - Optional menu props object with IVerticalMenuProps structure
 * @returns {Object} Display format message object
 */
export const transformApiMessageToDisplay = (apiMessageData, currentUserKey, menuProps) => {
  return transformSocketEventToMessage(apiMessageData, currentUserKey, menuProps);
};


// --- Main function: clean decision flow ---
export const generateFeedSettings = (feedDetails) => {
  if (!feedDetails) return [];

  const permissions = feedDetails.permissions;
  const tags = [confidentialityTag(isConfidentialFeed(feedDetails))];

  // Locked => only show Locked (plus the confidentiality/admin tag already added)
  if (isLockedFeed(feedDetails)) {
    return [...tags, lockedTag()];
  }

  // No permissions => return whatever we have so far
  if (!hasPermissions(permissions)) {
    return tags;
  }

  // Messaging disabled => View Only and stop
  if (isMessagingDisabled(permissions)) {
    return [...tags, viewOnlyTag()];
  }

  // Learners can't send => Learner View Only and stop
  if (learnersCantSend(permissions)) {
    return [...tags, learnerViewOnlyTag()];
  }

  // Message limit => add tag and continue (final return)
  return hasMessageLimit(permissions)
    ? [...tags, messageLimitTag(permissions)]
    : tags;
};

/**
 * Generate editor props based on permissions and feed details
 * Determines editor behavior, constraints, and UI state
 *
 * @param {Object} feedDetails - Feed details with permissions
 * @param {Function} sendMessage - Message sending function
 * @returns {Object} Editor props object
 */
export const generateEditorProps = (feedDetails, sendMessage) => {
  const permissions = feedDetails?.permissions;
  const baseProps = {
    placeholder: 'Type your message...',
    maxLength: PAGINATION_CONSTANTS.MAX_MESSAGE_LENGTH,
    onSendHandler: sendMessage,
    disableSendButton: false,
    hideEditor: false,
    isNewMessageVisiblityOn: true,
  };

  if (!permissions) return baseProps;

  if (feedDetails?.isLocked) {
    baseProps.hideEditor = true;
    baseProps.alertProps = {
      severity: 'warning',
      text: 'You cannot change the settings for this conversations because it is locked.',
    }
  }

  // Shut down conversation - hide text input field
  if (permissions.messaging_disabled) {
    baseProps.hideEditor = true;
    return baseProps;
  }

  // Message limit logic
  if (permissions?.limit_learner_messages) {
    if (permissions.learner_message_limit === 0 || (feedDetails?.limitReached?.learners?.length > 0)) {
      baseProps.alertProps = {
        severity: 'warning',
        text: feedDetails?.limitReached?.message,
      };
    }
  }
  return baseProps;
};

export const mapUnreadCounts = (unreadCountsData) => {
  // Handle empty or null response
  if (!unreadCountsData || !Array.isArray(unreadCountsData) || unreadCountsData.length === 0) {
    return {
      unreadAnnouncementsCount: 0,
      unreadMessagesCount: 0,
      unreadChannelsCount: 0,
    };
  }

  let unreadAnnouncementsCount = 0;
  let unreadMessagesCount = 0;
  let unreadChannelsCount = 0;

  // Map each feed type to its corresponding count
  unreadCountsData.forEach((item) => {
    const feedType = item.feedType?.toUpperCase();
    const count = item.totalUnread || 0;

    switch (feedType) {
      case 'ANNOUNCEMENT':
        unreadAnnouncementsCount += count;
        break;
      case 'DM':
        unreadMessagesCount += count;
        break;
      case 'CHANNEL':
        unreadChannelsCount += count;
        break;
      default:
        break;
    }
  });

  return {
    unreadAnnouncementsCount,
    unreadMessagesCount,
    unreadChannelsCount,
  };
};

export const getTotalUnreadCount = (unreadCounts) => {
  return unreadCounts.unreadAnnouncementsCount + unreadCounts.unreadMessagesCount + unreadCounts.unreadChannelsCount;
};

/**
 * Build a comma-separated string of member names,
 * excluding the current user based on both type + id match.
 *
 * @param {Array} membersInfo - Array of { type, id, name }
 * @param {string|number} currentUserKey - The id of the current user
 * @param {string} currentUserType - The type of the current user (e.g. 'PERSON')
 * @returns {string} Comma-separated member names
 */

export const buildMemberNames = (membersInfo = [], id, type) => {
  if (!Array.isArray(membersInfo) || membersInfo.length === 0) {
    return '';
  }

  return membersInfo
    .filter(member => member && member.name) // ensure valid
    .filter(member =>
      !(String(member.id) === String(id) && member.type === type)
    ) // exclude self by id + type
    .map(member => member.name)
    .join(', ');
};

/**
 * Build recipient list for announcements in the correct order
 * Orders recipients: Locations → Groups → Individuals
 * 
 * @param {Array} recipientInfo - Array of recipient objects with type, id, name
 * @returns {string} Comma-separated list of recipient names
 */
export const buildRecipientList = (recipientInfo = []) => {
  if (!Array.isArray(recipientInfo) || recipientInfo.length === 0) {
    return 'You';
  }

  // Separate by type
  const locations = recipientInfo.filter(r => r.type === MEMBERSHIP_TYPE.LOCATION);
  const groups = recipientInfo.filter(r => r.type === MEMBERSHIP_TYPE.GROUP);
  const persons = recipientInfo.filter(r => r.type === MEMBERSHIP_TYPE.PERSON);

  // Combine in order: locations, groups, persons
  const orderedRecipients = [...locations, ...groups, ...persons];

  // Return comma-separated names
  return orderedRecipients
    .filter(r => r && r.name)
    .map(r => r.name)
    .join(', ');
};

export const checkUserRoleType = (roleTypeCollection) => {
  return (USER_CONTEXT
    .details
    .locations
    .filter(location => location.active)
    .filter(location => roleTypeCollection.indexOf(location.roleType) > -1)).length > 0;
}

export const buildUpdatedPermissions = (conversationSettings, currentPermissions) => ({
  messaging_disabled: conversationSettings.shutDownConversation,
  learners_can_send_message: conversationSettings.learnerCanSendMessages,
  learner_can_reply: currentPermissions?.learner_can_reply ?? true,
  limit_learner_messages: conversationSettings.limitLearnerMessages,
  learner_message_limit: conversationSettings.numberOfMessages,
});

export const removeViewOnlyTag = tags => tags.filter(t => t.type !== TagEnum.ViewOnly);

export const addViewOnlyTag = tags => [...tags, { title: 'View Only', type: TagEnum.ViewOnly }];

export const generateAttachmentFileId = () => {
  if (crypto?.randomUUID) {
    return crypto.randomUUID();
  }
};

export const hasSelectedAttachmentFile = (selectedFile) => Boolean(selectedFile);

export const isAttachmentCountAllowed = (existingFilesCount = 0, rules = ATTACHMENT_UPLOAD) => {
  return existingFilesCount < rules.MAX_FILES;
};

const normalizeMimeType = (mimeType = '') => mimeType.toLowerCase().trim();

export const isAttachmentMimeTypeAllowed = (selectedFile, rules = ATTACHMENT_UPLOAD) => {
  if (!selectedFile?.type) {
    return false;
  }

  const selectedMimeType = normalizeMimeType(selectedFile.type);
  return rules.ALLOWED_MIME_TYPES.includes(selectedMimeType);
};

export const isAttachmentSizeAllowed = (selectedFile, rules = ATTACHMENT_UPLOAD) => {
  if (!selectedFile || typeof selectedFile.size !== 'number') {
    return false;
  }

  return selectedFile.size <= rules.MAX_FILE_SIZE_BYTES;
};

/**
 * Creates the base attachment upload item (no handlers).
 * Used by the hook to build the full list item with onRemove/onClick when validation passes.
 */
export const createAttachmentUploadItem = (selectedFile) => {
  if (!selectedFile) {
    return null;
  }

  return {
    fileId: generateAttachmentFileId(),
    fileName: selectedFile.name,
    file: selectedFile,
    size: selectedFile.size,
    mimeType: selectedFile.type,
    maxFileNameLength: ATTACHMENT_UPLOAD.MAX_FILE_NAME_LENGTH,
    presignedUrl: '',
    s3Key: '',
    expiresAt: '',
    uploadStatus: ATTACHMENT_FLOW_STATUS.UPLOADING,   //uploading status already marks loading as true by default
    loading: false,
    disabled: false,
    hasError: false,
    errorMessage: '',
    metadata: 'Uploading...',
    fileUrl: '',
  };
};

const escapeHtmlForFormSnippet = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Editor chip row for a form definition (same pipeline as file attachments above RichTextEditor).
 * @param {{ id: string|number, name?: string, schema?: Record<string, unknown> }} form
 * @param {(formId: string|number) => void} removeAttachedFormById
 * @param {{ onOpen: (fileId: string) => void | Promise<void>, onClose: () => void, isOpen: (fileId: string) => boolean }} preview
 */
export const createFormDefinitionEditorItem = (form, removeAttachedFormById, preview) => {
  if (!form?.id) {
    return null;
  }
  const { id, name, schema } = form;
  const fileId = `form-def-${id}`;
  const formSchema =
    schema && typeof schema === 'object' ? schema : undefined;
  return {
    fileId,
    fileName: name || 'Form',
    uploadStatus: ATTACHMENT_FLOW_STATUS.SUCCESS,
    metadata: 'Form',
    maxFileNameLength: ATTACHMENT_UPLOAD.MAX_FILE_NAME_LENGTH,
    formSchema,
    onRemove: () => {
      removeAttachedFormById(id);
    },
    onClick: () => {
      preview.onOpen(fileId);
    },
    isModalOpen: preview.isOpen(fileId),
    onModalClose: () => {
      preview.onClose();
    },
  };
};

/**
 * Appends form references to outgoing message HTML so the API carries which forms were attached.
 * @param {string} [html]
 * @param {Array<{ id: string|number, name?: string }>} [forms]
 */
export const appendFormReferencesToMessageHtml = (html, forms) => {
  if (!forms?.length) {
    return html || '';
  }
  const fragments = forms.map(
    (f) =>
      `<p data-form-definition-id="${escapeHtmlForFormSnippet(String(f.id))}">[Form: ${escapeHtmlForFormSnippet(f.name || 'Form')}]</p>`,
  );
  const suffix = fragments.join('');
  if (!html || !String(html).trim()) {
    return suffix;
  }
  return `${html}${suffix}`;
};

/**
 * Build create-message attachment entries for attached Form.io definitions.
 * Serialized via {@link getAttachmentFiles} to API: filename, empty s3Key, metadata.formId.
 * @param {Array<{ id: string|number, name?: string }>|undefined} forms
 */
export const buildAttachedFormMessageAttachments = (forms) => {
  if (!forms?.length) {
    return [];
  }
  return forms
    .filter((f) => f != null && f.id != null)
    .map((form) => {
      const formId = String(form.id);
      return {
        fileId: formId,
        filename: form.name || 'Form',
        mimeType: MIME_TYPE_FORM_IO,
        size: 1,
        s3Key: '',
        metadata: { formId: formId },
      };
    });
};

export const createAttachUploadPayload = (file) => {
  if (!file) {
    return null;
  }

  return {
    fileId: file.fileId,
    fileName: file.fileName,
    fileSize: file.size,
    mimeType: file.mimeType,
  };
};

export const getMaxFileSizeInMb = (rules = ATTACHMENT_UPLOAD) => {
  return Math.round((rules.MAX_FILE_SIZE_BYTES / (1024 * 1024)) * 10) / 10;
};

export const getFileSize = (size) => {
  if (typeof size !== 'number' || isNaN(size) || size < 0) return '';

  const KB = 1024;
  const MB = 1024 * KB;
  const GB = 1024 * MB;

  if (size < MB) {
    // < 1 MB → show in kB
    return `${(size / KB).toFixed(1)}kB`;
  }
  if (size < GB) {
    // 1 MB → < 1 GB → show in MB
    return `${(size / MB).toFixed(1)}MB`;
  }
  // ≥ 1 GB → show in GB
  return `${(size / GB).toFixed(1)}GB`;
};


export const validateAttachment = (selectedFile, existingFilesCount, rules = ATTACHMENT_UPLOAD) => {
  if (!hasSelectedAttachmentFile(selectedFile)) {
    return 'No file selected.';
  }

  if (!isAttachmentCountAllowed(existingFilesCount, rules)) {
    return `Maximum ${rules.MAX_FILES} files allowed.`;
  }

  if (!isAttachmentMimeTypeAllowed(selectedFile, rules)) {
    return `This file type is not supported. Use MP3, MP4, JPG, PNG, GIF, SVG, or PDF formats to upload.`;
  }

  if (!isAttachmentSizeAllowed(selectedFile, rules)) {
    return `File size is too large and exceeds the ${getMaxFileSizeInMb(rules)} MB limit`;
  }

  return '';
};


export const isFileObjectOrFileUrlValid = (file) => {
  return file && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';
};

export const safeCreateObjectURL = (file) => {
  try {
    if (isFileObjectOrFileUrlValid(file)) {
      return URL.createObjectURL(file);
    }
    return '';
  } catch {
    return '';
  }
};


export const revokeAttachmentFileUrl = (fileUrl) => {
  if (
    isFileObjectOrFileUrlValid(fileUrl)
  ) {
    URL.revokeObjectURL(fileUrl);
  }
};

export const attachmentMimeBase = (mimeType) =>
  typeof mimeType === 'string' ? mimeType.split(';')[0].trim() : '';

export const isRelativeUrlMimeType = (mimeType) =>
  RELATIVE_URL_MIME_TYPE === attachmentMimeBase(mimeType).toLowerCase();